import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import Localize from 'app/src/localize/Localize';
import GiftCardManager from 'app/src/payments/GiftCardManager';
import PaymentScanGiftCardView from 'app/src/views/payments/PaymentScanGiftCardView';

const _Events = {
  GIFT_CARD_SELECTED: 'gift:card:selected',
  LIST_GIFT_CARD: 'list:gift:card',
};

export default class PaymentScanGiftCardViewController extends BasePaymentController {
  static get Event() {
    return _Events;
  }

  /**
   * Constructor for a PaymentScanGiftCardViewController
   *
   * @param saleManager {SaleManager} The sale manager associated with the sale
   */
  constructor(saleManager, options = {}) {
    super(saleManager);

    this._options = options;

    // initialize the controller's managed view
    this.view = new PaymentScanGiftCardView();
  }

  /**
   * Sets the initial state of dynamic data within the view.
   */
  setInitialViewState() {
    // Focus the cursor on the Gift Card Input field
    this.view.setFocusOnGiftCardInput();
  }

  /**
   * Event handler for when the user has requested to scan (lookup) a gift card.
   *
   * @param cardNumber {string}   The gift card number
   */
  onSubmitGiftCardRequested(cardNumber) {
    if (GiftCardManager.omniGiftCardModuleEnabled()) {
      if (GiftCardManager.isValidCardNumber(cardNumber)) {
        return GiftCardManager.findGiftCardsByCode(cardNumber)
          .then(async (giftCards) => {
            if (giftCards?.length) {
              if (giftCards.length === 1 && giftCards[0].code === cardNumber) {
                this.fetchGiftCardDetailsHandler(cardNumber);
              } else {
                const giftCardsInfo = await this.getCustomersInfo(giftCards);
                this.trigger(_Events.LIST_GIFT_CARD, giftCardsInfo, this._options);
              }
            } else {
              this.noGiftCardFoundHandler(cardNumber);
            }
          })
          .catch(() => {
            alertUser(
              Localize.translate(
                'Service seems to be down while fetching gift card details. Please try again later.'
              )
            );
          });
      }
      alertUser(Localize.translate('Scan was not a gift card.'));
    }
    if (GiftCardManager.isValidCardNumber(cardNumber)) {
      this.fetchGiftCardDetailsHandler(cardNumber);
    } else {
      alertUser(Localize.translate('Scan was not a gift card.'));
    }
  }

  /**
   * @param giftCards {Array}
   * @return {Promise<Array>}
   */
  async getCustomersInfo(giftCards) {
    const requestIdArray = giftCards.map((card) => card.customerId).filter((id) => !!id);
    if (requestIdArray.length) {
      const customers = await GiftCardManager.getCustomers(requestIdArray);
      if (customers?.length) {
        return giftCards.map((card) => {
          const customer = customers.find((customer) => customer.customerID === card.customerId);
          return { code: card.code, name: this.getCustomerFullName(card, customer) };
        });
      }
    }
    return giftCards.map((card) => {
      return { code: card.code, name: this.getCustomerFullName(card, null) };
    });
  }

  /**
   * @param card {Object}
   * @param customer {Object}
   * @return {String}
   */
  getCustomerFullName(card, customer) {
    if (!customer) {
      return Localize.translate('No Customer is attached to the card');
    }
    if (!customer.firstName && !customer.lastName) {
      const system = card.customerSystem === 'retail' ? '' : 'eCom ';
      return `${system}Customer #${card.customerId}`;
    }
    return `${customer.firstName} ${customer.lastName}`;
  }

  /**
   * @param cardNumber {string}   The gift card number
   */
  noGiftCardFoundHandler(cardNumber) {
    if (this._saleManager.isRefundSale()) {
      // we automatically create a gift card when one doesn't exist for refunds
      GiftCardManager.createGiftCard(
        cardNumber,
        this._saleManager.customer ? this._saleManager.customer.get('customerID') : null
      ).done((giftCardModel) => {
        this.trigger(_Events.GIFT_CARD_SELECTED, giftCardModel, [], this._options);
      });
    } else {
      alertUser(Localize.translate('No gift card found.'));
    }
  }

  /**
   * @param cardNumber {string}   The gift card number
   */
  fetchGiftCardDetailsHandler(cardNumber) {
    GiftCardManager.fetchGiftCardDetails(cardNumber)
      .done((giftCardModel) => {
        if (giftCardModel.get('archived') === true) {
          // Show the disabled message
          let message = Localize.translate(
            `Gift <var>${giftCardModel.get('code')}</var> has been disabled.` +
              " <a href='/register.php?form_name=view&name=credit_account.views.gift_card&id=" +
              `${giftCardModel.id}'>View gift card record</a>.`
          );
          flashNotifier('messages', message, 15);
        } else {
          // we always allow refunding a gift card no matter what the available balance
          if (
            this._saleManager.isRefundSale() ||
            giftCardModel.availableCreditBalance.isGreaterThan(0)
          ) {
            this.trigger(_Events.GIFT_CARD_SELECTED, giftCardModel, [], this._options);
          } else {
            alertUser(Localize.translate('Gift card is empty.'));
          }
        }
      })
      .fail(() => {
        if (this._saleManager.isRefundSale()) {
          // we automatically create a gift card when one doesn't exist for refunds
          GiftCardManager.createGiftCard(
            cardNumber,
            this._saleManager.customer ? this._saleManager.customer.get('customerID') : null
          ).done((giftCardModel) => {
            this.trigger(_Events.GIFT_CARD_SELECTED, giftCardModel, [], this._options);
          });
        } else {
          alertUser(Localize.translate('No gift card found.'));
        }
      });
  }
}
